import { Box, HStack, Icon } from "@chakra-ui/react";
import CDS from "design-system";
import { FiClock, FiCheck, FiAlertTriangle } from "react-icons/fi";
import moment from "moment-timezone";
import { Participant } from "./types";

interface SelectionSummaryProps {
  selectedTime: Date | undefined;
  durationMinutes: number;
  timezone: string;
  participants: Participant[];
}

function getParticipantStatus(
  participant: Participant,
  selectedTime: Date,
  durationMinutes: number,
): "available" | "busy" | "outsideHours" {
  const startMs = selectedTime.getTime();
  const endMs = startMs + durationMinutes * 60 * 1000;

  const isBusy = participant.busyPeriods
    .filter((bp) => bp.source !== "current_meeting")
    .some((bp) => {
      const bpStart = new Date(bp.startTime).getTime();
      const bpEnd = new Date(bp.endTime).getTime();
      return bpStart < endMs && bpEnd > startMs;
    });
  if (isBusy) return "busy";

  if (participant.workingHours && participant.timezone) {
    const m = moment.tz(selectedTime, participant.timezone);
    const timeStr = m.format("HH:mm");
    if (timeStr < participant.workingHours.start || timeStr >= participant.workingHours.end) {
      return "outsideHours";
    }
  }

  return "available";
}

export default function SelectionSummary({
  selectedTime,
  durationMinutes,
  timezone,
  participants,
}: SelectionSummaryProps) {
  if (!selectedTime) {
    return (
      <Box px={2} py={3}>
        <CDS.Text12 color="gray.500">
          Click on the calendar to select a meeting time.
        </CDS.Text12>
      </Box>
    );
  }

  const startMoment = moment.tz(selectedTime, timezone);
  const endMoment = startMoment.clone().add(durationMinutes, "minutes");
  const timeLabel = `${startMoment.format("h:mm A")} – ${endMoment.format("h:mm A z")}`;

  const statuses = participants.map((p) => ({
    name: p.name,
    status: getParticipantStatus(p, selectedTime, durationMinutes),
  }));

  const busyNames = statuses.filter((s) => s.status === "busy").map((s) => s.name);
  const outsideNames = statuses.filter((s) => s.status === "outsideHours").map((s) => s.name);

  return (
    <Box px={2} py={3}>
      <HStack spacing={2} mb={1}>
        <Icon as={FiClock} boxSize={4} color="blue.500" />
        <CDS.Text12 fontWeight="semibold">
          {timeLabel}
        </CDS.Text12>
      </HStack>
      {busyNames.length === 0 && outsideNames.length === 0 ? (
        <HStack spacing={1} ml={6}>
          <Icon as={FiCheck} boxSize={3} color="green.500" />
          <CDS.Text10 color="green.600">
            All participants available
          </CDS.Text10>
        </HStack>
      ) : (
        <Box ml={6}>
          {busyNames.length > 0 && (
            <HStack spacing={1}>
              <Icon as={FiAlertTriangle} boxSize={3} color="red.500" />
              <CDS.Text10 color="red.600">
                {busyNames.join(", ")} {busyNames.length === 1 ? "has" : "have"} a conflict
              </CDS.Text10>
            </HStack>
          )}
          {outsideNames.length > 0 && (
            <HStack spacing={1}>
              <Icon as={FiAlertTriangle} boxSize={3} color="orange.500" />
              <CDS.Text10 color="orange.600">
                Outside working hours for {outsideNames.join(", ")}
              </CDS.Text10>
            </HStack>
          )}
        </Box>
      )}
    </Box>
  );
}
